import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import {
  getTasks, getTask, getLaborRates,
  getClientMasters, createClientMaster, updateClientMaster, deleteClientMaster,
  saveFullQuotation, getSavedQuotations, getSavedQuotation, deleteSavedQuotation,
  getTaskByStyleNo, getQuotationsByStyleNo,
  getDiamondRates, getDiamondShapes, getDiamondQualities,
  diamondRateLookup, settingRateLookup, getMasterSummary,
  uploadImage, getImageUrl, linkQuotationToTask
} from '@/utils/api';
import { ArrowLeft, Plus, Trash2, Download, FileSpreadsheet, Calculator, RefreshCw, FileText, Save, History, Users, Edit, Eye, Database, Upload, Sparkles } from 'lucide-react';
import api from '@/utils/api';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import { ClientSelect } from '@/lib/ClientSelect';
import {
  PIECE_TYPES, METAL_TYPES, KARATS, SETTING_TYPES,
  FALLBACK_DIAMOND_RATES, FALLBACK_SETTING_COSTS, FALLBACK_DIAMOND_TYPES,
  getCfpCost, getRhodiumCost, getFallbackSettingCost
} from '@/lib/quotation-utils';
import { STONE_TYPES, STONE_OWNERSHIPS, STONE_POSITION_CATEGORIES } from '@/utils/constants';

// New diamond row uses the CAD-aligned field names so quotes round-trip with
// CAD sheets without renaming. `rate`, `settingCostOverride`, and
// `settingCostFromDb` are quotation-only fields layered on top.
const blankDiamondRow = (id) => ({
  id,
  stone_category: 'Natural',
  ownership: 'KS Goods',
  stone_shape: 'Round',
  category: 'Center Stone',
  stone_quality: '',
  size_mm: '',
  weight_per_stone: 0,
  quantity: 0,
  setting_type: 'Prong',
  rate: 0,
  settingCostOverride: null,
  settingCostFromDb: null,
});

// Reshape an existing diamond (from a CAD task OR an old-shape saved quote)
// into the aligned in-memory shape. Tolerates both naming schemes.
const normalizeDiamondRow = (d, id) => ({
  id,
  stone_category: d.stone_category || 'Natural',
  ownership: d.ownership || 'KS Goods',
  stone_shape: d.stone_shape || d.type || 'Round',
  category: d.category || 'Center Stone',
  stone_quality: d.stone_quality || d.quality || '',
  size_mm: d.size_mm || '',
  weight_per_stone: Number(d.weight_per_stone ?? d.stone_weight_ct ?? d.weight ?? 0),
  quantity: Number(d.quantity ?? d.pieces ?? d.qty ?? 0),
  setting_type: d.setting_type || d.settingType || 'Prong',
  rate: Number(d.rate || 0),
  settingCostOverride: d.settingCostOverride ?? null,
  settingCostFromDb: null,
});
import { QuotationHistoryTab } from '@/components/quotation/QuotationHistoryTab';
import { QuotationClientsTab } from '@/components/quotation/QuotationClientsTab';

export default function QuotationEnginePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('calculator');

  const [laborRates, setLaborRates] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [loadingTask, setLoadingTask] = useState(false);

  // DB Master Data state
  const [usingMasterRates, setUsingMasterRates] = useState(false);
  const [dbDiamondRates, setDbDiamondRates] = useState([]);
  const [dbShapes, setDbShapes] = useState([]);
  const [dbQualityMap, setDbQualityMap] = useState({});
  const [dbSettingCache, setDbSettingCache] = useState({});

  // Client Masters
  const [clientMasters, setClientMasters] = useState([]);
  const [selectedClientMasterId, setSelectedClientMasterId] = useState('');
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [newClient, setNewClient] = useState({ name: '', code: '', default_metal: 'Gold', default_karat: '18', default_metal_rate: 0, usd_to_inr_rate: 83.5, default_stamping: 3, default_assembling: 0, default_cad_cost: 0, has_master_rates: false, notes: '' });

  // Saved Quotations
  const [savedQuotations, setSavedQuotations] = useState([]);
  const [loadingQuotations, setLoadingQuotations] = useState(false);

  // Currency & settings — `currency` is the authoring currency of the
  // quotation. Output (summary panel, PDF, Excel, history) shows ONLY this
  // currency. usdToInr is still kept so we can convert metal rates entered in
  // $/oz into per-gram costs in INR when the user is quoting in INR.
  const [usdToInr, setUsdToInr] = useState(83.50);
  const [currency, setCurrency] = useState('INR');
  const showInr = currency === 'INR';

  // Quote mode chooser — user picks CAD-imported vs direct quote before
  // touching the form. Skipped (chooser closed, mode = 'cad') when the page
  // is entered with a ?task= deep-link or after loading a saved quotation.
  const [quoteMode, setQuoteMode] = useState(null); // 'cad' | 'direct' | null
  const [showStartChooser, setShowStartChooser] = useState(true);
  // Image attached to a direct quote (no CAD task yet). Stored as the same
  // /api/uploads/... relative path uploadImage() returns.
  const [imageUrl, setImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  // Form fields
  const [metal, setMetal] = useState('Gold');
  const [karat, setKarat] = useState('18');
  const [metalLoss, setMetalLoss] = useState(10);
  const [metalRate, setMetalRate] = useState(0);
  const [netWeight, setNetWeight] = useState(0);
  const [pieceType, setPieceType] = useState('ring');
  const [stamping, setStamping] = useState(3);
  const [assembling, setAssembling] = useState(0);
  const [lockCost, setLockCost] = useState(0);
  const [enamel, setEnamel] = useState(0);
  const [milgrain, setMilgrain] = useState(0);
  const [cadCost, setCadCost] = useState(0);
  const [rhodium, setRhodium] = useState('None');
  const [rhodiumCostOverride, setRhodiumCostOverride] = useState(null);
  const [cfpCostOverride, setCfpCostOverride] = useState(null);
  const [handlingPercent, setHandlingPercent] = useState(2);
  const [micronPlating, setMicronPlating] = useState(false);
  const [micronThickness, setMicronThickness] = useState(0);
  const [micronCostPer, setMicronCostPer] = useState(0);
  const [diamonds, setDiamonds] = useState([blankDiamondRow(1)]);

  // Direct-quote image decoder (visible only when no task is loaded).
  // Uploads a reference image, runs it through the PD-AI Gemini decoder,
  // and pre-fills metal/piece type/diamond rows so the user starts from a
  // sane baseline instead of a blank form.
  const [decodingImage, setDecodingImage] = useState(false);
  const decodeFileRef = useRef(null);
  const [clientName, setClientName] = useState('');
  const [styleNo, setStyleNo] = useState('');
  const [styleNoVersions, setStyleNoVersions] = useState([]);
  const [quotationNotes, setQuotationNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Diamond type options based on mode
  const diamondTypes = useMemo(() => {
    if (usingMasterRates && dbShapes.length > 0) return dbShapes;
    return FALLBACK_DIAMOND_TYPES;
  }, [usingMasterRates, dbShapes]);

  // Get quality options for a given shape
  const getQualitiesForShape = useCallback((shape) => {
    if (usingMasterRates && dbQualityMap[shape]) return dbQualityMap[shape];
    if (FALLBACK_DIAMOND_RATES[shape]) return Object.keys(FALLBACK_DIAMOND_RATES[shape]);
    return [];
  }, [usingMasterRates, dbQualityMap]);

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ratesRes, tasksRes, clientsRes] = await Promise.all([
          getLaborRates(),
          getTasks(),
          getClientMasters()
        ]);
        setLaborRates(ratesRes.data || []);
        setTasks(tasksRes.data || []);
        const clients = clientsRes.data || [];
        setClientMasters(clients);

        // Auto-select DAI01 if it exists and has master rates
        const dai01 = clients.find(c => c.code === 'DAI01' && c.has_master_rates);
        if (dai01) {
          setSelectedClientMasterId(dai01.id);
          applyClientMasterData(dai01);
          loadMasterRates();
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
  }, []);

  // Load master rates from DB
  const loadMasterRates = async () => {
    try {
      const [ratesRes, shapesRes] = await Promise.all([
        getDiamondRates({ limit: 500 }),
        getDiamondShapes()
      ]);
      setDbDiamondRates(ratesRes.data || []);
      const shapes = shapesRes.data?.shapes || [];
      setDbShapes(shapes);

      // Load qualities for each shape
      const qualityMap = {};
      await Promise.all(shapes.map(async (shape) => {
        try {
          const qRes = await getDiamondQualities(shape);
          qualityMap[shape] = qRes.data?.qualities || [];
        } catch (err) { qualityMap[shape] = []; console.error(`Failed to load qualities for ${shape}:`, err); }
      }));
      setDbQualityMap(qualityMap);
      setUsingMasterRates(true);
      toast.success('Master rates loaded from database (DAI01)');
    } catch (error) {
      console.error('Failed to load master rates:', error);
      toast.error('Could not load master rates, using fallback');
      setUsingMasterRates(false);
    }
  };

  // Apply client master defaults
  const applyClientMasterData = (client) => {
    setClientName(client.name);
    setMetal(client.default_metal || 'Gold');
    setKarat(client.default_karat || '18');
    setMetalRate(client.default_metal_rate || 0);
    setUsdToInr(client.usd_to_inr_rate || 83.5);
    setStamping(client.default_stamping || 3);
    setAssembling(client.default_assembling || 0);
    setCadCost(client.default_cad_cost || 0);
  };

  // Apply client master presets
  const applyClientMaster = async (clientId) => {
    const client = clientMasters.find(c => c.id === clientId);
    if (!client) return;
    applyClientMasterData(client);

    if (client.has_master_rates) {
      await loadMasterRates();
    } else {
      setUsingMasterRates(false);
      setDbDiamondRates([]);
      setDbShapes([]);
      setDbQualityMap({});
    }
    toast.success(`Loaded presets for ${client.name} (${client.code})`);
  };

  // Fetch saved quotations when tab changes
  useEffect(() => {
    if (activeTab === 'history') fetchSavedQuotations();
  }, [activeTab]);

  const fetchSavedQuotations = async () => {
    setLoadingQuotations(true);
    try {
      const response = await getSavedQuotations({ limit: 100 });
      setSavedQuotations(response.data || []);
    } catch (error) {
      console.error('Failed to fetch quotations:', error);
    } finally {
      setLoadingQuotations(false);
    }
  };

  // Decode a reference image into form fields. Only meaningful in direct-quote
  // mode (no task loaded). Calls the PD-AI Gemini decoder and maps the
  // returned spec object to the quotation form.
  const handleDecodeImage = async (file) => {
    if (!file) return;
    setDecodingImage(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const base64 = String(dataUrl).split(',')[1] || '';
      if (!base64) throw new Error('Could not read image');

      const res = await api.post('/api/pd-ai/decode', {
        image_base64: base64,
        mime_type: file.type || 'image/jpeg',
      });
      const specs = res.data?.specs || {};

      const applied = [];

      // Piece type — map decoder's capitalized label to internal lowercase.
      const pieceMap = {
        Ring: 'ring', Pendant: 'pendant', Earring: 'earring', Earrings: 'earring',
        Bracelet: 'bracelet', Bangle: 'bangle', Necklace: 'necklace',
      };
      if (specs.product_type && pieceMap[specs.product_type]) {
        setPieceType(pieceMap[specs.product_type]);
        applied.push('piece type');
      }
      // Metal — only set if decoder returned a value the dropdown supports.
      if (specs.metal_type && METAL_TYPES.includes(specs.metal_type)) {
        setMetal(specs.metal_type);
        applied.push('metal');
      }
      if (specs.metal_karat) {
        const karatVal = String(specs.metal_karat).replace(/K$/i, '');
        if (KARATS.includes(karatVal)) {
          setKarat(karatVal);
          applied.push('karat');
        }
      }
      // Weight — total piece weight is the closest to "net weight" on the
      // quote form; the decoder also returns metal_weight_estimate_grams
      // but we prefer the total since the quote calculator grosses up.
      const wtEstimate = Number(specs.total_piece_weight_estimate_grams || specs.metal_weight_estimate_grams || 0);
      if (wtEstimate > 0) {
        setNetWeight(wtEstimate);
        applied.push('net weight');
      }
      // Stones → diamond rows. normalizeDiamondRow tolerates the decoder's
      // {type, shape, estimated_count, estimated_carat_total, setting_type}
      // shape — but we also accept `weight_per_stone` / `quantity` if present.
      if (Array.isArray(specs.stones) && specs.stones.length > 0) {
        const rows = specs.stones.map((s, idx) => {
          const qty = Number(s.estimated_total_in_complete_piece || s.estimated_count || s.quantity || 1);
          const totalCt = Number(s.estimated_carat_total || 0);
          const perStone = qty > 0 ? Number((totalCt / qty).toFixed(4)) : 0;
          return normalizeDiamondRow({
            stone_category: s.category === 'Center' || s.category === 'Accent'
              ? 'Natural'  // decoder's `category` is position, not natural/lab
              : 'Natural',
            stone_shape: s.shape,
            category: s.category && s.category !== 'Center'
              ? `${s.category} Stone`  // "Accent" → "Accent Stone"
              : 'Center Stone',
            stone_quality: s.specific_description || '',
            size_mm: s.estimated_size_mm || '',
            weight_per_stone: perStone,
            quantity: qty,
            setting_type: s.setting_type || 'Prong',
          }, idx + 1);
        });
        setDiamonds(rows);
        applied.push(`${rows.length} diamond row(s)`);
      }

      toast.success(applied.length
        ? `Decoded: filled ${applied.join(', ')}. Review before pricing.`
        : 'Decoded image, but no fields could be auto-filled');
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Image decode failed';
      toast.error(msg);
    } finally {
      setDecodingImage(false);
      if (decodeFileRef.current) decodeFileRef.current.value = '';
    }
  };

  const fetchClientMasters = async () => {
    try {
      const response = await getClientMasters();
      setClientMasters(response.data || []);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  };

  useEffect(() => {
    const taskId = searchParams.get('task');
    if (taskId) {
      setSelectedTaskId(taskId);
      loadTaskData(taskId);
      setQuoteMode('cad');
      setShowStartChooser(false);
    }
  }, [searchParams]);

  useEffect(() => {
    if (pieceType === 'bracelet' || pieceType === 'necklace') {
      setLockCost(25);
    } else {
      setLockCost(0);
    }
  }, [pieceType]);

  const loadTaskData = async (taskId) => {
    if (!taskId) return;
    setLoadingTask(true);
    try {
      const response = await getTask(taskId);
      const task = response.data;
      setClientName(task.client_name || '');
      setStyleNo(task.style_no || '');
      if (task.metal_type) setMetal(task.metal_type);
      if (task.metal_karat) setKarat(task.metal_karat.replace(/K$/i, ''));
      // Pull net_weight from task or from metal_details
      let taskNetWeight = task.net_weight;
      if (!taskNetWeight && task.metal_details?.length > 0) {
        taskNetWeight = task.metal_details.reduce((sum, m) => sum + (m.weight || m.net_weight || 0), 0);
      }
      if (taskNetWeight) setNetWeight(taskNetWeight);
      if (task.gross_weight && taskNetWeight) {
        const loss = ((task.gross_weight - taskNetWeight) / taskNetWeight) * 100;
        setMetalLoss(Math.round(loss * 100) / 100);
      }
      // Also pull karat from metal_details if flat field is missing
      if (!task.metal_karat && task.metal_details?.length > 0) {
        const mk = task.metal_details[0].metal_karat || task.metal_details[0].karat;
        if (mk) setKarat(mk.replace(/K$/i, ''));
      }
      const pieceMap = { 'Ring': 'ring', 'Pendant': 'pendant', 'Earring': 'earring', 'Bracelet': 'bracelet', 'Bangle': 'bangle', 'Necklace': 'necklace' };
      if (task.piece_type && pieceMap[task.piece_type]) setPieceType(pieceMap[task.piece_type]);
      if (task.rhodium_plating && task.rhodium_plating !== 'None') setRhodium('Rhodium');
      if (task.diamonds && task.diamonds.length > 0) {
        const loadedDiamonds = task.diamonds.map((d, idx) => normalizeDiamondRow(d, idx + 1));
        setDiamonds(loadedDiamonds.length > 0 ? loadedDiamonds : [blankDiamondRow(1)]);
        // Rates will be looked up after state updates
      }
      if (task.style_no) {
        try {
          const versionsRes = await getQuotationsByStyleNo(task.style_no);
          setStyleNoVersions(versionsRes.data || []);
        } catch (err) { setStyleNoVersions([]); console.error('Failed to load style versions:', err); }
      }
      toast.success('Task data loaded');
    } catch (error) {
      toast.error('Failed to load task data');
    } finally {
      setLoadingTask(false);
    }
  };

  const loadSavedQuotation = async (quotationId) => {
    try {
      const response = await getSavedQuotation(quotationId);
      const q = response.data;
      setClientName(q.client_name || '');
      setStyleNo(q.style_no || '');
      setMetal(q.metal || 'Gold');
      setKarat(q.karat || '18');
      setMetalLoss(q.metal_loss_percent || 10);
      setMetalRate(q.metal_rate_per_oz || 0);
      setNetWeight(q.net_weight || 0);
      setPieceType(q.piece_type || 'ring');
      setStamping(q.stamping || 3);
      setAssembling(q.assembling || 0);
      setLockCost(q.lock_cost || 0);
      setEnamel(q.enamel || 0);
      setMilgrain(q.milgrain || 0);
      setCadCost(q.cad_cost || 0);
      setRhodium(q.rhodium || 'None');
      setUsdToInr(q.usd_to_inr_rate || 83.5);
      setQuotationNotes(q.notes || '');
      if (q.diamonds && q.diamonds.length > 0) {
        // Saved quotes from older versions used the simplified {type, quality, weight, qty, …}
        // shape. normalizeDiamondRow handles both old + new shapes.
        setDiamonds(q.diamonds.map((d, i) => normalizeDiamondRow(d, i + 1)));
      }
      setSelectedTaskId(q.task_id || '');
      setSelectedClientMasterId(q.client_master_id || '');
      setCurrency((q.currency || 'INR').toUpperCase());
      setImageUrl(q.image_url || '');
      setQuoteMode(q.task_id ? 'cad' : 'direct');
      setShowStartChooser(false);
      setActiveTab('calculator');
      toast.success(`Loaded quotation ${q.ref}`);
    } catch (error) {
      toast.error('Failed to load quotation');
    }
  };

  // Look up diamond rate from DB
  const lookupDiamondRate = useCallback(async (shape, quality, caratWeight) => {
    if (!usingMasterRates || !shape || !quality) return null;
    try {
      const res = await diamondRateLookup(shape, quality, caratWeight || 0.01);
      return res.data?.rate || null;
    } catch (err) { console.error('Diamond rate lookup failed:', err); return null; }
  }, [usingMasterRates]);

  // Look up setting rate from DB
  const lookupSettingRate = useCallback(async (stoneShape, settingType, weightCt) => {
    if (!usingMasterRates) return null;
    const key = `${stoneShape}-${settingType}-${weightCt}`;
    if (dbSettingCache[key] !== undefined) return dbSettingCache[key];
    try {
      const res = await settingRateLookup(stoneShape, settingType, weightCt || 0);
      const rate = res.data?.rate ?? null;
      setDbSettingCache(prev => ({ ...prev, [key]: rate }));
      return rate;
    } catch (err) { console.error('Setting rate lookup failed:', err); return null; }
  }, [usingMasterRates, dbSettingCache]);

  // Get diamond rate (sync, from DB cache or fallback)
  const getDiamondRate = useCallback((type, quality) => {
    if (usingMasterRates) {
      // Find from cached DB rates
      const match = dbDiamondRates.find(r => r.shape === type && r.rm_code === quality);
      return match ? match.sale_rate : 0;
    }
    if (FALLBACK_DIAMOND_RATES[type] && FALLBACK_DIAMOND_RATES[type][quality]) return FALLBACK_DIAMOND_RATES[type][quality];
    return 0;
  }, [usingMasterRates, dbDiamondRates]);

  // Get setting cost (sync, from DB cache or fallback)
  const getSettingCost = useCallback((type, weight, stoneShape = 'Round') => {
    const key = `${stoneShape}-${type}-${weight}`;
    if (usingMasterRates && dbSettingCache[key] !== undefined) return dbSettingCache[key];
    return getFallbackSettingCost(type, weight);
  }, [usingMasterRates, dbSettingCache]);

  const addDiamondRow = () => {
    const firstShape = diamondTypes[0] || 'Round';
    const qualities = getQualitiesForShape(firstShape);
    const firstQuality = qualities[0] || '';
    setDiamonds([...diamonds, {
      ...blankDiamondRow(Date.now()),
      stone_shape: firstShape,
      stone_quality: firstQuality,
      rate: getDiamondRate(firstShape, firstQuality),
    }]);
  };

  const removeDiamondRow = (id) => {
    if (diamonds.length === 1) return;
    setDiamonds(diamonds.filter(d => d.id !== id));
  };

  const updateDiamond = async (id, field, value) => {
    setDiamonds(prev => prev.map(d => {
      if (d.id !== id) return d;
      const updated = { ...d, [field]: value };
      if (field === 'stone_shape') {
        const qualities = getQualitiesForShape(value);
        updated.stone_quality = qualities[0] || '';
        updated.rate = getDiamondRate(value, updated.stone_quality);
      }
      if (field === 'stone_quality') {
        updated.rate = getDiamondRate(d.stone_shape, value);
      }
      return updated;
    }));

    // For weight-dependent rate lookups using DB
    if (usingMasterRates && (field === 'weight_per_stone' || field === 'stone_shape' || field === 'stone_quality')) {
      const diamond = diamonds.find(d => d.id === id);
      if (!diamond) return;
      const shape = field === 'stone_shape' ? value : diamond.stone_shape;
      const quality = field === 'stone_quality'
        ? value
        : (field === 'stone_shape' ? (getQualitiesForShape(value)[0] || '') : diamond.stone_quality);
      const weight = field === 'weight_per_stone' ? parseFloat(value) || 0 : diamond.weight_per_stone;

      if (shape && quality && weight > 0) {
        const rate = await lookupDiamondRate(shape, quality, weight);
        if (rate !== null) {
          setDiamonds(prev => prev.map(d => d.id === id ? { ...d, rate } : d));
        }
      }
    }

    // Setting cost DB lookup
    if (usingMasterRates && (field === 'setting_type' || field === 'weight_per_stone' || field === 'stone_shape')) {
      const diamond = diamonds.find(d => d.id === id);
      if (!diamond) return;
      const stoneShape = field === 'stone_shape' ? value : diamond.stone_shape;
      const settingType = field === 'setting_type' ? value : diamond.setting_type;
      const weight = field === 'weight_per_stone' ? parseFloat(value) || 0 : diamond.weight_per_stone;
      const settingRate = await lookupSettingRate(stoneShape, settingType, weight);
      if (settingRate !== null) {
        setDiamonds(prev => prev.map(d => d.id === id ? { ...d, settingCostFromDb: settingRate } : d));
      }
    }
  };

  // Calculations
  const calculations = useMemo(() => {
    // LBMA formula: (rate_per_oz / 31.104 / 0.999) * purity_factor
    const KARAT_PURITY = { '9': 0.375, '10': 0.4167, '14': 0.5833, '18': 0.75, '22': 0.9167, 'P950': 0.95 };
    const purity = KARAT_PURITY[karat] || 0.75;
    const pureGoldPerGram = metalRate / 31.104 / 0.999;
    // Metal loss is a $/gm markup, not a weight markup. 10% loss on a $100/gm base → $110/gm.
    // Gross weight is kept as informational only (net + same % for reference).
    const baseCostPerGramUsd = pureGoldPerGram * purity;
    const costPerGramUsd = baseCostPerGramUsd * (1 + (metalLoss || 0) / 100);
    const grossWeight = netWeight + (metalLoss / 100 * netWeight);
    const metalCostUsd = costPerGramUsd * netWeight;

    const cfpCalc = getCfpCost(metal, netWeight, pieceType, laborRates);
    const cfpCostUsd = cfpCostOverride !== null ? cfpCostOverride : cfpCalc;
    const rhodiumCalc = rhodium === 'Rhodium' ? getRhodiumCost(pieceType, laborRates) : 0;
    let platingCostUsd = rhodiumCostOverride !== null ? rhodiumCostOverride : rhodiumCalc;
    if (micronPlating) platingCostUsd += micronThickness * micronCostPer;
    const totalLabourUsd = cfpCostUsd + stamping + platingCostUsd + enamel + milgrain + lockCost + assembling;
    const labourWithCadUsd = totalLabourUsd + cadCost;

    let totalDiamondCostUsd = 0, totalSettingCostUsd = 0, totalDiamondWeight = 0, totalDiamondPieces = 0;
    const diamondDetails = diamonds.map(d => {
      const wt = d.weight_per_stone || 0;
      const qty = d.quantity || 0;
      const amount = (d.rate || 0) * wt * qty;
      const settingAutoCalc = d.settingCostFromDb !== null
        ? d.settingCostFromDb
        : getSettingCost(d.setting_type, wt, d.stone_shape);
      const settingPerStone = d.settingCostOverride !== null ? d.settingCostOverride : settingAutoCalc;
      const settingCost = settingPerStone * qty;
      totalDiamondCostUsd += amount;
      totalSettingCostUsd += settingCost;
      totalDiamondWeight += wt * qty;
      totalDiamondPieces += qty;
      return { ...d, amount, settingCost, settingPerStone };
    });
    const handlingCostUsd = totalDiamondCostUsd * (handlingPercent / 100);
    const finalProductionCostUsd = metalCostUsd + labourWithCadUsd + totalDiamondCostUsd + totalSettingCostUsd + handlingCostUsd;
    return {
      costPerGramUsd, pureGoldPerGram, purity, grossWeight, metalCostUsd, cfpCostUsd, cfpCalc, platingCostUsd, rhodiumCalc, totalLabourUsd, labourWithCadUsd,
      totalDiamondCostUsd, totalSettingCostUsd, totalDiamondWeight, totalDiamondPieces, handlingCostUsd, finalProductionCostUsd, diamondDetails,
      costPerGramInr: costPerGramUsd * usdToInr, metalCostInr: metalCostUsd * usdToInr, totalLabourInr: totalLabourUsd * usdToInr,
      labourWithCadInr: labourWithCadUsd * usdToInr, totalDiamondCostInr: totalDiamondCostUsd * usdToInr,
      totalSettingCostInr: totalSettingCostUsd * usdToInr, handlingCostInr: handlingCostUsd * usdToInr, finalProductionCostInr: finalProductionCostUsd * usdToInr
    };
  }, [metal, metalRate, netWeight, metalLoss, karat, pieceType, stamping, assembling, lockCost, enamel, milgrain, cadCost, rhodium, rhodiumCostOverride, cfpCostOverride, handlingPercent, micronPlating, micronThickness, micronCostPer, diamonds, laborRates, usdToInr]);

  const formatCurrency = (value, ccy = 'USD') => {
    if (ccy === 'INR') return '\u20b9' + value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  // Plain-text variant for PDF/Excel \u2014 uses "INR" prefix instead of the
  // \u20b9 glyph because default PDF/print fonts often lack it and render a
  // tofu box. Stick to ASCII in exported documents.
  const formatCurrencyPlain = (value, ccy = 'USD') => {
    if (value === undefined || value === null || isNaN(value)) value = 0;
    if (ccy === 'INR') return 'INR ' + value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return 'USD ' + value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  // Pick the right cost value for the active currency in this quotation \u2014
  // calculations carry both USD and INR variants, but output should only
  // surface the authoring currency.
  const inCcy = (usdField, inrField) => (showInr ? calculations[inrField] : calculations[usdField]);

  // Save Quotation
  const handleSaveQuotation = async () => {
    if (!clientName.trim()) { toast.error('Please enter client name'); return; }
    setSaving(true);
    try {
      const data = {
        task_id: selectedTaskId || null,
        style_no: styleNo || null,
        client_master_id: selectedClientMasterId || null,
        client_name: clientName,
        piece_type: pieceType,
        metal, karat,
        metal_loss_percent: metalLoss,
        metal_rate_per_oz: metalRate,
        net_weight: netWeight,
        gross_weight: calculations.grossWeight,
        cfp_cost: calculations.cfpCostUsd,
        stamping, assembling, lock_cost: lockCost, enamel, milgrain, cad_cost: cadCost,
        plating_cost: calculations.platingCostUsd,
        rhodium,
        diamonds: diamonds.map(d => ({
          stone_category: d.stone_category,
          ownership: d.ownership,
          stone_shape: d.stone_shape,
          category: d.category,
          stone_quality: d.stone_quality,
          size_mm: d.size_mm,
          weight_per_stone: d.weight_per_stone,
          quantity: d.quantity,
          setting_type: d.setting_type,
          rate: d.rate,
        })),
        metal_cost_usd: calculations.metalCostUsd,
        total_labour_usd: calculations.labourWithCadUsd,
        total_diamond_cost_usd: calculations.totalDiamondCostUsd,
        total_setting_cost_usd: calculations.totalSettingCostUsd,
        handling_cost_usd: calculations.handlingCostUsd,
        final_cost_usd: calculations.finalProductionCostUsd,
        usd_to_inr_rate: usdToInr,
        final_cost_inr: calculations.finalProductionCostInr,
        currency,
        image_url: imageUrl || null,
        notes: quotationNotes
      };
      const response = await saveFullQuotation(data);
      toast.success(`Quotation saved! Ref: ${response.data.ref}`);
      if (styleNo) {
        try {
          const versionsRes = await getQuotationsByStyleNo(styleNo);
          setStyleNoVersions(versionsRes.data || []);
        } catch (err) { console.error('Failed to reload style versions:', err); }
      }
      fetchSavedQuotations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save quotation');
    } finally {
      setSaving(false);
    }
  };

  // Client Master CRUD
  const handleSaveClient = async () => {
    try {
      if (editingClient) {
        await updateClientMaster(editingClient.id, newClient);
        toast.success('Client updated');
      } else {
        await createClientMaster(newClient);
        toast.success('Client created');
      }
      setShowClientDialog(false);
      setEditingClient(null);
      setNewClient({ name: '', code: '', default_metal: 'Gold', default_karat: '18', default_metal_rate: 0, usd_to_inr_rate: 83.5, default_stamping: 3, default_assembling: 0, default_cad_cost: 0, has_master_rates: false, notes: '' });
      fetchClientMasters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save client');
    }
  };

  const handleDeleteClient = async (id) => {
    if (!window.confirm('Delete this client master?')) return;
    try {
      await deleteClientMaster(id);
      toast.success('Client deleted');
      fetchClientMasters();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const res = await uploadImage(file);
      const path = res.data.image || res.data.url || res.data.filename;
      if (path) setImageUrl(path);
      toast.success('Image attached');
    } catch (err) {
      console.error('Image upload failed:', err);
      toast.error('Image upload failed');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleDeleteQuotation = async (id) => {
    if (!window.confirm('Delete this quotation?')) return;
    try {
      await deleteSavedQuotation(id);
      toast.success('Quotation deleted');
      fetchSavedQuotations();
    } catch (error) {
      toast.error('Failed to delete quotation');
    }
  };

  // Download master rate card as Excel
  const handleDownloadMasterRates = async (clientCode) => {
    try {
      toast.info('Fetching master rates...');
      const [diamondRes, laborRes, summaryRes] = await Promise.all([
        getDiamondRates({ limit: 500 }),
        getLaborRates(),
        getMasterSummary()
      ]);
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();

      // Diamond Rates sheet
      const diamondData = (diamondRes.data || []).map(r => ({
        'Shape': r.shape, 'RM Code': r.rm_code, 'Rate Type': r.rate_type,
        'From Size': r.from_size, 'To Size': r.to_size,
        'Cost Rate': r.cost_rate, 'Sale Rate': r.sale_rate,
        'Customer Code': r.customer_code
      }));
      if (diamondData.length > 0) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(diamondData), 'Diamond Rates');

      // Labour Rates sheet
      const laborData = (laborRes.data || []).map(r => ({
        'Process': r.process_code, 'Item': r.item_code, 'Rate Type': r.rate_type,
        'From Weight': r.from_weight, 'To Weight': r.to_weight,
        'Cost Rate': r.cost_rate, 'Sale Rate': r.sale_rate,
        'Customer Code': r.customer_code
      }));
      if (laborData.length > 0) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(laborData), 'Labour Rates');

      // Summary sheet
      const summary = summaryRes.data || {};
      const summaryData = [
        { 'Category': 'Diamond Rates', 'Count': summary.diamond_rates?.count || 0, 'Details': `Shapes: ${(summary.diamond_rates?.shapes || []).join(', ')}` },
        { 'Category': 'Labour Rates', 'Count': summary.labor_rates?.count || 0, 'Details': `Processes: ${(summary.labor_rates?.processes || []).join(', ')}` },
        { 'Category': 'Pointer Conversion', 'Count': summary.pointer_conversion?.count || 0, 'Details': `Shapes: ${(summary.pointer_conversion?.shapes || []).join(', ')}` },
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryData), 'Summary');

      XLSX.writeFile(wb, `MasterRates_${clientCode}_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Master rates downloaded');
    } catch (err) {
      console.error('Failed to download master rates:', err);
      toast.error('Failed to download master rates');
    }
  };


  // Export functions
  const exportToExcel = async () => {
    const XLSX = await import('xlsx');
    const wb = XLSX.utils.book_new();
    const ccy = currency;
    const fmt = (usdField, inrField) => formatCurrencyPlain(showInr ? calculations[inrField] : calculations[usdField], ccy);
    const summaryData = [
      ['JEWELRY QUOTATION'], [''],
      ['Client Name:', clientName],
      ['Date:', new Date().toLocaleDateString()],
      ['Currency:', ccy], [''],
      ['METAL DETAILS'],
      ['Metal Type:', metal],
      ['Karat:', karat],
      ['Net Weight (g):', netWeight],
      ['Gross Weight (g):', calculations.grossWeight.toFixed(2)],
      ['Metal Rate ($/oz):', metalRate], [''],
      [`COST SUMMARY (${ccy})`],
      ['Metal Cost:', fmt('metalCostUsd', 'metalCostInr')],
      ['Labour + CAD:', fmt('labourWithCadUsd', 'labourWithCadInr')],
      ['Diamond Cost:', fmt('totalDiamondCostUsd', 'totalDiamondCostInr')],
      ['Setting Cost:', fmt('totalSettingCostUsd', 'totalSettingCostInr')],
      ['Handling:', fmt('handlingCostUsd', 'handlingCostInr')], [''],
      ['FINAL COST:', fmt('finalProductionCostUsd', 'finalProductionCostInr')],
    ];
    if (showInr) summaryData.push([''], ['Exchange Rate:', `1 USD = INR ${usdToInr}`]);
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summaryData), 'Summary');
    const rateLabel = showInr ? 'Rate (INR/ct)' : 'Rate ($/ct)';
    const amountLabel = `Amount (${ccy})`;
    const setLabel = `Setting Cost (${ccy})`;
    const convert = (v) => showInr ? v * usdToInr : v;
    const diamondRows = [
      ['Stone Category', 'Ownership', 'Cut', 'Position', 'Quality', 'Size (mm)', 'Wt/stone (ct)', 'Qty', 'Total ct', rateLabel, amountLabel, 'Setting', setLabel],
      ...calculations.diamondDetails.map(d => [
        d.stone_category || '',
        d.ownership || '',
        d.stone_shape || '',
        d.category || '',
        d.stone_quality || '',
        d.size_mm || '',
        d.weight_per_stone || 0,
        d.quantity || 0,
        ((d.weight_per_stone || 0) * (d.quantity || 0)).toFixed(3),
        convert(d.rate).toFixed(2),
        convert(d.amount).toFixed(2),
        d.setting_type || '',
        convert(d.settingCost).toFixed(2),
      ]),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(diamondRows), 'Diamonds');
    XLSX.writeFile(wb, `Quotation_${clientName || 'Draft'}_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel exported');
  };

  const exportToPdf = () => {
    const ccy = currency;
    const convert = (v) => showInr ? v * usdToInr : v;
    const fmt = (usdField, inrField) => formatCurrencyPlain(showInr ? calculations[inrField] : calculations[usdField], ccy);
    const imageBlock = imageUrl ? `<div style="text-align:center;margin-bottom:20px"><img src="${imageUrl.startsWith('http') ? imageUrl : (process.env.REACT_APP_BACKEND_URL || '') + imageUrl}" style="max-width:240px;max-height:240px;border:1px solid #ddd;border-radius:6px" /></div>` : '';
    const fxLine = showInr ? `Exchange Rate: 1 USD = INR ${usdToInr} | ` : '';
    const printContent = `<!DOCTYPE html><html><head><title>Quotation - ${clientName}</title><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Segoe UI',Arial,sans-serif;padding:40px;color:#333;line-height:1.6}.header{text-align:center;margin-bottom:30px;border-bottom:3px solid #d4af37;padding-bottom:20px}.header h1{color:#1a365d;font-size:28px}.section{margin-bottom:20px}.section-title{background:#1a365d;color:white;padding:10px 15px;font-size:14px;font-weight:600}table{width:100%;border-collapse:collapse;font-size:13px}th,td{padding:10px;text-align:left;border-bottom:1px solid #eee}th{background:#f8f9fa}.text-right{text-align:right}.grand-total{background:#1a365d;color:white;font-size:16px}</style></head><body><div class="header"><h1>K's JEWELLERY</h1><p>Production Quotation (${ccy})</p></div>${imageBlock}<div class="section"><div class="section-title">DETAILS</div><table><tr><td>Client: <strong>${clientName}</strong></td><td>Metal: <strong>${metal} ${karat}K</strong></td><td>Weight: <strong>${netWeight}g (${calculations.grossWeight.toFixed(2)}g gross)</strong></td></tr></table></div><div class="section"><div class="section-title">DIAMONDS (${calculations.totalDiamondPieces} pcs / ${calculations.totalDiamondWeight.toFixed(3)} ct)</div><table><thead><tr><th>Stone</th><th>Cut</th><th>Position</th><th>Quality</th><th class="text-right">Size (mm)</th><th class="text-right">Wt/stone</th><th class="text-right">Qty</th><th class="text-right">Rate (${ccy})</th><th class="text-right">Amount (${ccy})</th></tr></thead><tbody>${calculations.diamondDetails.map(d => `<tr><td>${d.stone_category || ''}</td><td>${d.stone_shape || ''}</td><td>${d.category || ''}</td><td>${d.stone_quality || ''}</td><td class="text-right">${d.size_mm || ''}</td><td class="text-right">${d.weight_per_stone || 0}</td><td class="text-right">${d.quantity || 0}</td><td class="text-right">${formatCurrencyPlain(convert(d.rate), ccy)}</td><td class="text-right">${formatCurrencyPlain(convert(d.amount), ccy)}</td></tr>`).join('')}</tbody></table></div><div class="section"><div class="section-title">COST SUMMARY (${ccy})</div><table><tr><td>Metal Cost</td><td class="text-right">${fmt('metalCostUsd','metalCostInr')}</td></tr><tr><td>Labour + CAD</td><td class="text-right">${fmt('labourWithCadUsd','labourWithCadInr')}</td></tr><tr><td>Diamond Cost</td><td class="text-right">${fmt('totalDiamondCostUsd','totalDiamondCostInr')}</td></tr><tr><td>Setting Cost</td><td class="text-right">${fmt('totalSettingCostUsd','totalSettingCostInr')}</td></tr><tr><td>Handling (${handlingPercent}%)</td><td class="text-right">${fmt('handlingCostUsd','handlingCostInr')}</td></tr><tr class="grand-total"><td><strong>FINAL PRODUCTION COST</strong></td><td class="text-right"><strong>${fmt('finalProductionCostUsd','finalProductionCostInr')}</strong></td></tr></table></div><p style="margin-top:20px;font-size:12px;color:#666">${fxLine}Generated: ${new Date().toLocaleString()}</p></body></html>`;
    const blob = new Blob([printContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, '_blank');
    if (printWindow) {
      printWindow.addEventListener('load', () => {
        printWindow.print();
        URL.revokeObjectURL(url);
      });
    }
    toast.success('PDF ready');
  };

  const isAdmin = ['admin', 'master_admin', 'design_head'].includes(user?.role);

  return (
    <CollapsibleLayout>
    <div className="min-h-[calc(100vh-48px)] bg-slate-50">
      <header className="bg-slate-900 text-white px-6 py-3 sticky top-12 z-40">
        <div className="flex items-center justify-between max-w-[1600px] mx-auto">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-lg font-semibold" data-testid="qt-page-title">Jewelry Quotation Engine</h1>
              <p className="text-slate-400 text-xs">Calculate, save & manage production quotations</p>
            </div>
            {usingMasterRates && (
              <span className="ml-2 px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" data-testid="qt-master-badge">
                <Database className="w-3 h-3 inline mr-1" />DAI01 Master Rates
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={exportToExcel} className="bg-transparent border-slate-600 text-white hover:bg-slate-800 h-7 text-xs" data-testid="qt-export-excel"><FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Excel</Button>
            <Button variant="outline" size="sm" onClick={exportToPdf} className="bg-transparent border-slate-600 text-white hover:bg-slate-800 h-7 text-xs" data-testid="qt-export-pdf"><FileText className="w-3.5 h-3.5 mr-1" /> PDF</Button>
            <Button size="sm" onClick={handleSaveQuotation} disabled={saving} className="bg-amber-500 hover:bg-amber-600 h-7 text-xs" data-testid="qt-save-btn"><Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving...' : 'Save'}</Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="calculator" className="flex items-center gap-2" data-testid="qt-tab-calculator"><Calculator className="w-4 h-4" /> Calculator</TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2" data-testid="qt-tab-history"><History className="w-4 h-4" /> Saved ({savedQuotations.length})</TabsTrigger>
            <TabsTrigger value="clients" className="flex items-center gap-2" data-testid="qt-tab-clients"><Users className="w-4 h-4" /> Clients</TabsTrigger>
          </TabsList>

          {/* Calculator Tab */}
          <TabsContent value="calculator">
            {/* Start chooser: import from CAD or make a direct quote.
                Hidden once the user picks (or a ?task= deep-link lands). */}
            <Dialog open={showStartChooser} onOpenChange={setShowStartChooser}>
              <DialogContent className="sm:max-w-md" data-testid="qt-start-chooser">
                <DialogHeader>
                  <DialogTitle>Start a new quotation</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-slate-500 -mt-2">Pick how you want to begin. You can always link a direct quote to a CAD task later from the Saved tab.</p>
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <button
                    onClick={() => { setQuoteMode('cad'); setShowStartChooser(false); }}
                    className="border rounded-lg p-4 text-left hover:bg-amber-50 hover:border-amber-400 transition-colors"
                    data-testid="qt-start-cad"
                  >
                    <div className="flex items-center gap-2 mb-1"><FileText className="w-4 h-4 text-amber-600" /><span className="font-semibold text-sm">Import from CAD</span></div>
                    <p className="text-xs text-slate-500">Pull weight, metal, stones from an existing task.</p>
                  </button>
                  <button
                    onClick={() => { setQuoteMode('direct'); setShowStartChooser(false); }}
                    className="border rounded-lg p-4 text-left hover:bg-amber-50 hover:border-amber-400 transition-colors"
                    data-testid="qt-start-direct"
                  >
                    <div className="flex items-center gap-2 mb-1"><Calculator className="w-4 h-4 text-amber-600" /><span className="font-semibold text-sm">Direct Quote</span></div>
                    <p className="text-xs text-slate-500">Quote from scratch. Attach a reference image; link to a CAD later.</p>
                  </button>
                </div>
              </DialogContent>
            </Dialog>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                {quoteMode && (
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span>Mode:</span>
                    <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">{quoteMode === 'cad' ? 'CAD-imported' : 'Direct quote'}</span>
                    <button onClick={() => setShowStartChooser(true)} className="text-amber-600 hover:underline">change</button>
                  </div>
                )}
                {/* Task & Client Selection */}
                <Card>
                  <CardHeader className="py-3"><CardTitle className="text-base">Load Data</CardTitle></CardHeader>
                  <CardContent className="py-3 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">From Task</Label>
                        <div className="flex gap-2 mt-1">
                          <Select value={selectedTaskId} onValueChange={setSelectedTaskId}>
                            <SelectTrigger data-testid="qt-task-select"><SelectValue placeholder="Select task..." /></SelectTrigger>
                            <SelectContent>{tasks.map(t => <SelectItem key={t.id} value={t.id}>{t.internal_cad_no} - {t.client_name}</SelectItem>)}</SelectContent>
                          </Select>
                          <Button onClick={() => loadTaskData(selectedTaskId)} disabled={!selectedTaskId || loadingTask} size="sm" data-testid="qt-load-task-btn">{loadingTask ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Load'}</Button>
                        </div>
                        {/* Direct-quote helper: only useful when there's no
                            task linked. Hidden once a task is loaded so we
                            don't accidentally overwrite real CAD data. */}
                        {!selectedTaskId && (
                          <div className="mt-2 flex items-center gap-2">
                            <input
                              ref={decodeFileRef}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleDecodeImage(e.target.files?.[0])}
                              data-testid="qt-decode-image-input"
                            />
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => decodeFileRef.current?.click()}
                              disabled={decodingImage}
                              className="text-xs gap-1.5"
                              data-testid="qt-decode-image-btn"
                            >
                              {decodingImage
                                ? <><RefreshCw className="w-3 h-3 animate-spin" /> Decoding…</>
                                : <><Sparkles className="w-3 h-3" /> Decode from image</>}
                            </Button>
                            <span className="text-[10px] text-slate-400">No CAD? Drop a reference photo, AI fills the form.</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">From Client Preset</Label>
                        <div className="flex gap-2 mt-1">
                          <Select value={selectedClientMasterId} onValueChange={(v) => { setSelectedClientMasterId(v); applyClientMaster(v); }}>
                            <SelectTrigger data-testid="qt-client-preset-select"><SelectValue placeholder="Select client..." /></SelectTrigger>
                            <SelectContent>{clientMasters.map(c => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.name} ({c.code}) {c.has_master_rates ? ' [DB Rates]' : ''}
                              </SelectItem>
                            ))}</SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <ClientSelect value={clientName} onChange={v => setClientName(v)} placeholder="Select client..." className="w-full" />
                      <Input placeholder="Style No" value={styleNo} onChange={(e) => setStyleNo(e.target.value)} data-testid="qt-style-no" />
                      <div className="flex gap-2 items-center">
                        <Label className="text-xs whitespace-nowrap">USD&rarr;INR:</Label>
                        <Input type="number" value={usdToInr} onChange={(e) => setUsdToInr(parseFloat(e.target.value) || 83.5)} className="w-24" step="0.01" data-testid="qt-usd-inr" />
                        <Button variant={showInr ? 'default' : 'outline'} onClick={() => setCurrency(showInr ? 'USD' : 'INR')} className={showInr ? 'bg-amber-500 hover:bg-amber-600' : ''} data-testid="qt-currency-toggle">{showInr ? '\u20b9 INR' : '$ USD'}</Button>
                      </div>
                    </div>
                    {/* Reference Image — useful for direct quotes so the
                        PDF and saved record carry a picture of the piece.
                        Also editable when importing from CAD if the user
                        wants to override the source image. */}
                    <div className="flex items-center gap-3 pt-1">
                      <Label className="text-xs text-slate-500 whitespace-nowrap">Reference Image:</Label>
                      {imageUrl ? (
                        <div className="flex items-center gap-2">
                          <img src={getImageUrl(imageUrl)} alt="Quote reference" className="h-14 w-14 object-cover rounded border" data-testid="qt-image-preview" />
                          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setImageUrl('')} data-testid="qt-image-clear">Remove</Button>
                        </div>
                      ) : (
                        <label className="cursor-pointer inline-flex items-center gap-1 px-2 py-1 rounded border border-dashed border-amber-300 text-xs text-amber-700 hover:bg-amber-50 transition-colors" data-testid="qt-image-upload-label">
                          {uploadingImage ? 'Uploading…' : 'Attach Image'}
                          <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="qt-image-upload-input" />
                        </label>
                      )}
                    </div>
                    {/* Version History for current Style No */}
                    {styleNo && styleNoVersions.length > 0 && (
                      <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                        <p className="text-xs font-medium text-amber-800 mb-1">Existing Quotation Versions for {styleNo}:</p>
                        <div className="flex flex-wrap gap-2">
                          {styleNoVersions.map(v => (
                            <button key={v.id} onClick={() => loadSavedQuotation(v.id)} className="text-xs px-2 py-1 rounded bg-white border border-amber-300 hover:bg-amber-100 text-amber-700 font-mono" data-testid={`qt-version-${v.version}`}>
                              {v.ref} &mdash; ${v.final_cost_usd?.toFixed(2)}
                            </button>
                          ))}
                        </div>
                        <p className="text-[10px] text-amber-600 mt-1">Saving will create version #{styleNoVersions.length + 1}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Metal & CFP */}
                <Card>
                  <CardHeader className="py-3 bg-slate-100 rounded-t-lg"><CardTitle className="text-base">Metal & CFP</CardTitle></CardHeader>
                  <CardContent className="py-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                      <div><Label className="text-xs text-slate-500">Metal</Label><Select value={metal} onValueChange={setMetal}><SelectTrigger className="mt-1" data-testid="qt-metal-select"><SelectValue /></SelectTrigger><SelectContent>{METAL_TYPES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
                      <div><Label className="text-xs text-slate-500">Karat</Label><Select value={karat} onValueChange={setKarat}><SelectTrigger className="mt-1" data-testid="qt-karat-select"><SelectValue /></SelectTrigger><SelectContent>{KARATS.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent></Select></div>
                      <div><Label className="text-xs text-slate-500">Metal Loss (%)</Label><Input type="number" className="mt-1" value={metalLoss} onChange={(e) => setMetalLoss(parseFloat(e.target.value) || 0)} data-testid="qt-metal-loss" /></div>
                      <div><Label className="text-xs text-slate-500">Metal Rate ($/oz)</Label><Input type="number" className="mt-1" value={metalRate} onChange={(e) => setMetalRate(parseFloat(e.target.value) || 0)} data-testid="qt-metal-rate" /></div>
                      <div><Label className="text-xs text-slate-500">Net Weight (g)</Label><Input type="number" className="mt-1" value={netWeight} onChange={(e) => setNetWeight(parseFloat(e.target.value) || 0)} data-testid="qt-net-weight" /></div>
                      <div><Label className="text-xs text-slate-500">Piece Type</Label><Select value={pieceType} onValueChange={setPieceType}><SelectTrigger className="mt-1" data-testid="qt-piece-type"><SelectValue /></SelectTrigger><SelectContent>{PIECE_TYPES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select></div>
                    </div>
                  </CardContent>
                </Card>

                {/* Labour */}
                <Card>
                  <CardHeader className="py-3 bg-slate-100 rounded-t-lg"><CardTitle className="text-base">Labour & Finish (USD)</CardTitle></CardHeader>
                  <CardContent className="py-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      <div>
                        <Label className="text-xs text-slate-500">CFP Cost {cfpCostOverride !== null ? '' : <span className="text-emerald-500 text-[9px]">(auto)</span>}</Label>
                        <Input type="number" className={`mt-1 ${cfpCostOverride !== null ? '' : 'bg-slate-50'}`} step="0.01"
                          value={cfpCostOverride !== null ? cfpCostOverride : calculations.cfpCalc.toFixed(2)}
                          onChange={(e) => setCfpCostOverride(parseFloat(e.target.value) || 0)}
                          onDoubleClick={() => setCfpCostOverride(null)}
                          title="Double-click to reset to auto"
                          data-testid="qt-cfp-cost" />
                      </div>
                      <div><Label className="text-xs text-slate-500">Stamping</Label><Input type="number" className="mt-1" value={stamping} onChange={(e) => setStamping(parseFloat(e.target.value) || 0)} data-testid="qt-stamping" /></div>
                      <div><Label className="text-xs text-slate-500">Assembling</Label><Input type="number" className="mt-1" value={assembling} onChange={(e) => setAssembling(parseFloat(e.target.value) || 0)} data-testid="qt-assembling" /></div>
                      <div><Label className="text-xs text-slate-500">Lock Cost</Label><Input type="number" className="mt-1" value={lockCost} onChange={(e) => setLockCost(parseFloat(e.target.value) || 0)} data-testid="qt-lock-cost" /></div>
                      <div><Label className="text-xs text-slate-500">Enamel</Label><Input type="number" className="mt-1" value={enamel} onChange={(e) => setEnamel(parseFloat(e.target.value) || 0)} data-testid="qt-enamel" /></div>
                      <div><Label className="text-xs text-slate-500">Milgrain</Label><Input type="number" className="mt-1" value={milgrain} onChange={(e) => setMilgrain(parseFloat(e.target.value) || 0)} data-testid="qt-milgrain" /></div>
                      <div><Label className="text-xs text-slate-500">CAD Cost</Label><Input type="number" className="mt-1" value={cadCost} onChange={(e) => setCadCost(parseFloat(e.target.value) || 0)} data-testid="qt-cad-cost" /></div>
                      <div><Label className="text-xs text-slate-500">Rhodium</Label><Select value={rhodium} onValueChange={(v) => { setRhodium(v); if (v === 'None') setRhodiumCostOverride(null); }}><SelectTrigger className="mt-1" data-testid="qt-rhodium"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="None">None</SelectItem><SelectItem value="Rhodium">Rhodium</SelectItem></SelectContent></Select></div>
                      {rhodium === 'Rhodium' && (
                        <div>
                          <Label className="text-xs text-slate-500">Rhodium $ {rhodiumCostOverride !== null ? '' : <span className="text-emerald-500 text-[9px]">(auto)</span>}</Label>
                          <Input type="number" className={`mt-1 ${rhodiumCostOverride !== null ? '' : 'bg-slate-50'}`} step="0.01"
                            value={rhodiumCostOverride !== null ? rhodiumCostOverride : calculations.rhodiumCalc.toFixed(2)}
                            onChange={(e) => setRhodiumCostOverride(parseFloat(e.target.value) || 0)}
                            onDoubleClick={() => setRhodiumCostOverride(null)}
                            title="Double-click to reset to auto"
                            data-testid="qt-rhodium-cost" />
                        </div>
                      )}
                      <div><Label className="text-xs text-slate-500">Handling %</Label><Input type="number" className="mt-1" step="0.1" value={handlingPercent} onChange={(e) => setHandlingPercent(parseFloat(e.target.value) || 0)} data-testid="qt-handling-pct" /></div>
                    </div>
                  </CardContent>
                </Card>

                {/* Diamonds */}
                <Card>
                  <CardHeader className="py-3 bg-slate-100 rounded-t-lg flex flex-row items-center justify-between">
                    <CardTitle className="text-base">
                      Diamonds
                      {usingMasterRates && <span className="ml-2 text-[10px] font-normal text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">DB Rates</span>}
                    </CardTitle>
                    <Button size="sm" onClick={addDiamondRow} className="bg-amber-500 hover:bg-amber-600" data-testid="qt-add-diamond"><Plus className="w-4 h-4 mr-1" /> Add</Button>
                  </CardHeader>
                  <CardContent className="py-4 overflow-x-auto">
                    {/* Columns match the CAD sheet's stone table (Stone Category,
                        Ownership, Cut, Position, Quality, Size, Wt) plus the
                        quotation-specific Qty/Rate/Amount/Setting/Set$ trailing
                        cost columns. Wider tables — uses horizontal scroll on
                        narrow screens rather than collapsing columns. */}
                    <table className="w-full min-w-[1280px] text-xs">
                      <thead>
                        <tr className="border-b border-slate-200 text-slate-500 font-medium">
                          {['Stone Category', 'Ownership', 'Cut', 'Position', 'Quality', 'Size (mm)', 'Wt/stone (ct)', 'Qty', 'Total ct', 'Rate', 'Amount', 'Setting', 'Set $/pc', ''].map(h => (
                            <th key={h} className="text-left py-1.5 px-1">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {diamonds.map((d) => {
                          const qualities = getQualitiesForShape(d.stone_shape);
                          const wt = d.weight_per_stone || 0;
                          const qty = d.quantity || 0;
                          const total_ct = wt * qty;
                          const amount = (d.rate || 0) * wt * qty;
                          const detail = calculations.diamondDetails.find(dd => dd.id === d.id);
                          const settingPerStone = detail?.settingPerStone || 0;
                          return (
                            <tr key={d.id} className="border-b border-slate-100" data-testid={`qt-diamond-row-${d.id}`}>
                              <td className="py-1 px-1">
                                <Select value={d.stone_category} onValueChange={(v) => updateDiamond(d.id, 'stone_category', v)}>
                                  <SelectTrigger className="h-8 text-[11px]"><SelectValue /></SelectTrigger>
                                  <SelectContent>{STONE_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                </Select>
                              </td>
                              <td className="py-1 px-1">
                                <Select value={d.ownership} onValueChange={(v) => updateDiamond(d.id, 'ownership', v)}>
                                  <SelectTrigger className="h-8 text-[11px]"><SelectValue /></SelectTrigger>
                                  <SelectContent>{STONE_OWNERSHIPS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                </Select>
                              </td>
                              <td className="py-1 px-1">
                                <Select value={d.stone_shape} onValueChange={(v) => updateDiamond(d.id, 'stone_shape', v)}>
                                  <SelectTrigger className="h-8 text-[11px]"><SelectValue /></SelectTrigger>
                                  <SelectContent>{diamondTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                                </Select>
                              </td>
                              <td className="py-1 px-1">
                                <Select value={d.category} onValueChange={(v) => updateDiamond(d.id, 'category', v)}>
                                  <SelectTrigger className="h-8 text-[11px]"><SelectValue /></SelectTrigger>
                                  <SelectContent>{STONE_POSITION_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                </Select>
                              </td>
                              <td className="py-1 px-1">
                                {qualities.length > 0 ? (
                                  <Select value={d.stone_quality} onValueChange={(v) => updateDiamond(d.id, 'stone_quality', v)}>
                                    <SelectTrigger className="h-8 text-[11px]"><SelectValue placeholder="Quality" /></SelectTrigger>
                                    <SelectContent>{qualities.map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
                                  </Select>
                                ) : (
                                  <Input className="h-8 text-[11px]" value={d.stone_quality} onChange={(e) => updateDiamond(d.id, 'stone_quality', e.target.value)} placeholder="Quality" />
                                )}
                              </td>
                              <td className="py-1 px-1">
                                <Input className="h-8 text-[11px]" value={d.size_mm} onChange={(e) => updateDiamond(d.id, 'size_mm', e.target.value)} placeholder="mm" />
                              </td>
                              <td className="py-1 px-1">
                                <Input type="number" className="h-8 text-[11px]" step="0.00001" min="0" value={d.weight_per_stone || ''} onChange={(e) => updateDiamond(d.id, 'weight_per_stone', parseFloat(e.target.value) || 0)} data-testid={`qt-diamond-weight-${d.id}`} />
                              </td>
                              <td className="py-1 px-1">
                                <Input type="number" className="h-8 text-[11px]" value={d.quantity || ''} onChange={(e) => updateDiamond(d.id, 'quantity', parseInt(e.target.value) || 0)} data-testid={`qt-diamond-qty-${d.id}`} />
                              </td>
                              <td className="py-1 px-1 text-[11px] text-slate-500 text-center">{total_ct.toFixed(2)}</td>
                              <td className="py-1 px-1">
                                <Input type="number" className="h-8 text-[11px]" value={d.rate || ''} onChange={(e) => updateDiamond(d.id, 'rate', parseFloat(e.target.value) || 0)} data-testid={`qt-diamond-rate-${d.id}`} />
                              </td>
                              <td className="py-1 px-1">
                                <Input type="number" className="h-8 text-[11px] bg-slate-50 font-medium" value={amount.toFixed(2)} readOnly />
                              </td>
                              <td className="py-1 px-1">
                                <Select value={d.setting_type} onValueChange={(v) => updateDiamond(d.id, 'setting_type', v)}>
                                  <SelectTrigger className="h-8 text-[11px]"><SelectValue /></SelectTrigger>
                                  <SelectContent>{SETTING_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                                </Select>
                              </td>
                              <td className="py-1 px-1">
                                <Input type="number" className="h-8 text-[11px]" step="0.01"
                                  value={d.settingCostOverride !== null ? d.settingCostOverride : settingPerStone.toFixed(2)}
                                  onChange={(e) => updateDiamond(d.id, 'settingCostOverride', parseFloat(e.target.value) || 0)}
                                  onDoubleClick={() => updateDiamond(d.id, 'settingCostOverride', null)}
                                  title="Double-click to reset to auto"
                                  data-testid={`qt-diamond-setting-cost-${d.id}`} />
                              </td>
                              <td className="py-1 px-1">
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => removeDiamondRow(d.id)} disabled={diamonds.length === 1} data-testid={`qt-diamond-remove-${d.id}`}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>

                {/* Notes */}
                <Card>
                  <CardHeader className="py-3 bg-slate-100 rounded-t-lg"><CardTitle className="text-base">Notes</CardTitle></CardHeader>
                  <CardContent className="py-3"><textarea className="w-full p-3 border rounded-md text-sm" rows={2} placeholder="Add notes..." value={quotationNotes} onChange={(e) => setQuotationNotes(e.target.value)} data-testid="qt-notes" /></CardContent>
                </Card>
              </div>

              {/* Right - Results */}
              <div>
                <Card className="sticky top-24">
                  <CardHeader className="py-3 bg-slate-800 text-white rounded-t-lg">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2"><Calculator className="w-4 h-4" /> Summary</CardTitle>
                      <span className="text-xs text-amber-400">{showInr ? '\u20b9 INR' : '$ USD'}</span>
                    </div>
                  </CardHeader>
                  <CardContent className="py-4 space-y-2 text-sm" data-testid="qt-summary-panel">
                    <div className="flex justify-between"><span className="text-slate-500">Pure Gold/g</span><span>{formatCurrency(calculations.pureGoldPerGram)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Purity ({karat})</span><span>{(calculations.purity * 100).toFixed(1)}%</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Cost/gram</span><span>{showInr ? formatCurrency(calculations.costPerGramInr, 'INR') : formatCurrency(calculations.costPerGramUsd)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Gross Weight</span><span>{calculations.grossWeight.toFixed(2)}g</span></div>
                    <div className="border-t pt-2"><div className="flex justify-between font-medium"><span>Metal Cost</span><span>{showInr ? formatCurrency(calculations.metalCostInr, 'INR') : formatCurrency(calculations.metalCostUsd)}</span></div></div>
                    <div className="border-t pt-2"><div className="flex justify-between"><span className="text-slate-500">Total Labour</span><span>{showInr ? formatCurrency(calculations.totalLabourInr, 'INR') : formatCurrency(calculations.totalLabourUsd)}</span></div><div className="flex justify-between font-medium"><span>Labour + CAD</span><span>{showInr ? formatCurrency(calculations.labourWithCadInr, 'INR') : formatCurrency(calculations.labourWithCadUsd)}</span></div></div>
                    <div className="border-t pt-2"><p className="text-xs text-slate-400 mb-1">{calculations.totalDiamondPieces} pcs / {calculations.totalDiamondWeight.toFixed(3)} ct</p><div className="flex justify-between font-medium"><span>Diamond Cost</span><span>{showInr ? formatCurrency(calculations.totalDiamondCostInr, 'INR') : formatCurrency(calculations.totalDiamondCostUsd)}</span></div><div className="flex justify-between"><span className="text-slate-500">Setting Cost</span><span>{showInr ? formatCurrency(calculations.totalSettingCostInr, 'INR') : formatCurrency(calculations.totalSettingCostUsd)}</span></div><div className="flex justify-between"><span className="text-slate-500">Handling ({handlingPercent}%)</span><span>{showInr ? formatCurrency(calculations.handlingCostInr, 'INR') : formatCurrency(calculations.handlingCostUsd)}</span></div></div>
                    <div className="border-t-2 border-amber-500 pt-3 mt-3 bg-amber-50 -mx-4 px-4 py-3 rounded-b-lg">
                      <div className="text-center">
                        <p className="text-slate-600 font-medium mb-1">Final Production Cost</p>
                        <p className="text-2xl font-bold text-amber-600" data-testid="qt-final-cost">
                          {showInr
                            ? '\u20b9' + calculations.finalProductionCostInr.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            : '$' + calculations.finalProductionCostUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-center text-slate-400 pt-2">Rate: 1 USD = ₹{usdToInr}</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <QuotationHistoryTab
              savedQuotations={savedQuotations}
              loadingQuotations={loadingQuotations}
              fetchSavedQuotations={fetchSavedQuotations}
              loadSavedQuotation={loadSavedQuotation}
              handleDeleteQuotation={handleDeleteQuotation}
            />
          </TabsContent>

          {/* Clients Tab */}
          <TabsContent value="clients">
            <QuotationClientsTab
              clientMasters={clientMasters}
              isAdmin={isAdmin}
              showClientDialog={showClientDialog}
              setShowClientDialog={setShowClientDialog}
              editingClient={editingClient}
              setEditingClient={setEditingClient}
              newClient={newClient}
              setNewClient={setNewClient}
              handleSaveClient={handleSaveClient}
              handleDeleteClient={handleDeleteClient}
              applyClientMaster={applyClientMaster}
              setSelectedClientMasterId={setSelectedClientMasterId}
              setActiveTab={setActiveTab}
              handleDownloadMasterRates={handleDownloadMasterRates}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
    </CollapsibleLayout>
  );
}
